import { computed, reactive, watch } from 'vue'
import { createSharedComposable } from '@vueuse/core'
import { useQueryClient } from '@tanstack/vue-query'
import type {
  AssignmentV2_GetAssignmentInfoResponse,
  AssignmentV2_GetAssignmentInfoResponse_UserInfo,
  AssignmentV2_UserAssignment,
} from '@zendesk/zqa-services/assignments_v2'
import { session } from '@/composables/useSession'
import useTasks from '@/composables/useTasks'
import useTicketRouteParams from '@/composables/useTicketRouteParams'
import useTaskListFilters from '@/modules/tasks/composables/useTaskListFilters'
import { tasksAssignmentKeys } from '@/modules/tasks/queries/keys'

export type GoalState = Pick<
  AssignmentV2_UserAssignment,
  'goal' | 'personalGoal' | 'reviewCount' | 'personalReviewCount' | 'cycleEnd'
>

export default createSharedComposable(() => {
  const queryClient = useQueryClient()
  const { allAssignments, currFinished } = useTasks()
  const { assignmentId } = useTicketRouteParams()
  const { assignmentInfo } = useTaskListFilters()
  const currentAssignment = computed(() =>
    allAssignments.value.find((assignment) => assignment.id === assignmentId.value),
  )

  const goalState: GoalState = reactive({
    goal: 0,
    personalGoal: 0,
    reviewCount: 0,
    personalReviewCount: 0,
    cycleEnd: null,
  })

  const updateGoalState = (data: AssignmentV2_GetAssignmentInfoResponse) => {
    const { personalGoal, personalReviewCount } = getPersonalGoal(data.reviewers)

    goalState.cycleEnd = data?.cycle?.end || null
    goalState.goal = data?.goal || 0
    goalState.reviewCount = data?.reviewCount || 0
    goalState.personalGoal = personalGoal
    goalState.personalReviewCount = personalReviewCount
  }

  const syncGoalStateToAssignmentsCache = () => {
    const assignment = currentAssignment.value

    if (!assignmentId.value || !session.workspace?.id || !assignment || !goalState.goal) return

    const updatedAssignment = {
      ...assignment,
      goal: goalState.goal,
      reviewCount: goalState.reviewCount,
      cycleEnd: goalState.cycleEnd,
      ...(goalState.personalGoal ? { personalGoal: goalState.personalGoal } : {}),
      ...(goalState.personalReviewCount ? { personalReviewCount: goalState.personalReviewCount } : {}),
    }

    queryClient.setQueryData<{ assignments: AssignmentV2_UserAssignment[] }>(
      tasksAssignmentKeys.list(session.workspace.id),
      (oldData) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          assignments: oldData.assignments.map((item) => (item.id === assignmentId.value ? updatedAssignment : item)),
        }
      },
    )

    if (
      assignment.reviewCount &&
      assignment.reviewCount !== goalState.reviewCount &&
      goalState.goal === goalState.reviewCount
    ) {
      currFinished.value = [updatedAssignment]
    }
  }

  function getPersonalGoal(reviewers: AssignmentV2_GetAssignmentInfoResponse_UserInfo[]) {
    const reviewer = reviewers?.find((r) => r.user?.id === session.user.id?.toString())

    return {
      personalGoal: reviewer?.goal || 0,
      personalReviewCount: reviewer?.reviewCount || 0,
    }
  }

  watch(
    assignmentInfo,
    (newData) => {
      if (newData) {
        updateGoalState(newData)
        syncGoalStateToAssignmentsCache()
      }
    },
    { immediate: true },
  )
  
  return { goalState }
})
